import { DownloadOutlined, SyncOutlined } from "@ant-design/icons";
import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useDownloadBriefXlsx } from "@entities/brief/lib/hooks/useDownloadBriefXlsx";
import { useSyncBriefGoogleSheets } from "@entities/brief/lib/hooks/useSyncBriefGoogleSheets";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { AddCreator } from "@features/AddCreator";
import { BulkEditBriefInfluencersModal } from "@features/AddCreator/ui/BulkEditModal";
import { BriefCustomFields } from "@features/BriefCustomFields";
import { CampaignDeliverables } from "@features/CampaignDeliverables";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { BriefNotificationActionType } from "@shared/lib/types/brief";
import {
  IBriefInfluencerSearchRequestV2,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";
import { appendHttp } from "@shared/lib/utils/appendHttp";
import { getSocialMediaUrl } from "@shared/lib/utils/getSocialMediaUrl";
import { openUrlsInNewTabs } from "@shared/lib/utils/openUrlsInNewTabs";
import { Button } from "@shared/ui/Button";
import { useBriefInfluencerFilterStore } from "@widgets/CampaignBrief/store/useBriefInfluencerFilterStore";
import { Drawer, Dropdown, Space } from "antd";
import {
  ChevronDown,
  ExternalLink,
  LayoutDashboard,
  MoreHorizontal,
  Package,
  Pencil,
  Sparkles,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface BriefInfDropDownProps {
  triggerClassName?: string;
  searchParams?: Partial<IBriefInfluencerSearchRequestV2>;
  selectedRows?: IBriefInfluencerV2[];
}

const getInfluencerProfileUrl = (
  influencer: IBriefInfluencerV2
): string | undefined => {
  const link = influencer.socialMedia?.link;
  if (link) {
    return appendHttp(link);
  }

  return getSocialMediaUrl(
    influencer.socialMedia?.platform,
    influencer.socialMedia?.username
  );
};

export default function BriefInfDropDown({
  triggerClassName,
  searchParams,
  selectedRows,
}: BriefInfDropDownProps) {
  const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
  const [isDeliverablesDrawerOpen, setIsDeliverablesDrawerOpen] =
    useState(false);
  const [openCreatorModal, setOpenCreatorModal] = useState<boolean>(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const keepDropdownOpenRef = useRef(false);

  const { brief, briefId } = useBriefStore();
  const { beFilters, setShowDeclinedCreators } =
    useBriefInfluencerFilterStore();
  const showDeclinedCreators = beFilters.showDeclinedCreators;
  const { syncGoogleSheet, isSyncing, variables } =
    useSyncBriefGoogleSheets(briefId);
  const { downloadBriefXlsx, isDownloadingXlsx } = useDownloadBriefXlsx();
  const { userDetails } = useGetMe();
  const { bulkActions, isBulkActionsSuccess, isBulkActions } = useBulkActions();

  const isAccountManagerSheetBusy =
    (isSyncing && variables?.type === "accountManager") || isDownloadingXlsx;

  useEffect(() => {
    if (isAccountManagerSheetBusy) {
      keepDropdownOpenRef.current = true;
      setDropdownOpen(true);
      return;
    }
    keepDropdownOpenRef.current = false;
  }, [isAccountManagerSheetBusy]);

  const handleDropdownOpenChange = (open: boolean) => {
    if (!open && (keepDropdownOpenRef.current || isAccountManagerSheetBusy)) {
      return;
    }
    setDropdownOpen(open);
  };

  const keepOpenAndRun = (action: () => void) => {
    keepDropdownOpenRef.current = true;
    setDropdownOpen(true);
    action();
  };

  const handleOpenLinksInNewTabs = () => {
    if (!selectedRows?.length) return;

    const urls = [
      ...new Set(
        selectedRows
          .map(getInfluencerProfileUrl)
          .filter((url): url is string => Boolean(url))
      ),
    ];

    if (urls.length === 0) {
      ToastService.error("No profile links found for selected creators");
      return;
    }

    const { openedCount, blockedUrls } = openUrlsInNewTabs(urls);

    if (blockedUrls.length > 0) {
      ToastService.error(
        `Opened ${openedCount} of ${urls.length} tabs. Allow pop-ups for this site to open all links.`
      );
    }
  };

  const handleBulkAnalyze = () => {
    if (!searchParams || !briefId) return;

    const { limit, ...filterParams } = searchParams;

    bulkActions({
      briefId,
      actionType: BriefNotificationActionType.analyzeCreator,
      ids: [],
      notIncludeIds: [],
      isSelectAll: true,
      filterParams,
      actionParams: {},
    });
  };

  useEffect(() => {
    if (isBulkActionsSuccess) {
      ToastService.success("Analyzing filtered creators...");
    }
  }, [isBulkActionsSuccess]);

  const disableSheet =
    !userDetails?.userEntity?.permissions?.includes(
      "export-brief-google-sheet"
    ) && !userDetails?.userEntity?.permissions?.includes("super");
  const items = [
    {
      key: "manageColumns",
      label: (
        <Space>
          <LayoutDashboard size={14} />
          <span>Manage Columns</span>
        </Space>
      ),
      onClick: () => {
        setIsManageColumnsOpen(true);
      },
    },
    {
      key: "manageDeliverables",
      label: (
        <Space>
          <Package size={14} />
          <span>Manage Deliverables</span>
        </Space>
      ),
      onClick: () => {
        setIsDeliverablesDrawerOpen(true);
      },
    },
    {
      key: "bulkEditCreators",
      label: (
        <Space>
          <Pencil size={14} />
          <span>Bulk Edit Creators</span>
        </Space>
      ),
      onClick: () => {
        setIsBulkEditOpen(true);
      },
    },
    ...(selectedRows && selectedRows.length > 0
      ? [
          {
            key: "openLinksInNewTab",
            label: (
              <Space>
                <ExternalLink size={14} />
                <span>Open Links in new tab</span>
              </Space>
            ),
            onClick: handleOpenLinksInNewTabs,
          },
        ]
      : []),
    ...(searchParams
      ? [
          {
            key: "bulkAnalyze",
            label: (
              <Space>
                <Sparkles size={14} />
                <span>Bulk Analyze</span>
              </Space>
            ),
            disabled: isBulkActions,
            onClick: handleBulkAnalyze,
          },
        ]
      : []),
    {
      key: "showDeclinedCreators",
      label: showDeclinedCreators
        ? "Hide Declined Creators"
        : "Show Declined Creators",
      onClick: () => setShowDeclinedCreators(!showDeclinedCreators),
    },

    {
      key: "accountManager",
      label: (
        <Space>
          {(isSyncing && variables?.type === "accountManager") ||
          isDownloadingXlsx ? (
            <SyncOutlined spin />
          ) : null}
          <span>Account Manager</span>
        </Space>
      ),
      children: [
        ...(brief?.accountManagerGoogleSheetId
          ? [
              {
                key: "viewAccountManagerSheet",
                label: "View Sheet Link",
                title: disableSheet
                  ? "You don't have permission to perform this action."
                  : "",
                disabled: disableSheet,
                onClick: () => {
                  window.open(
                    `https://docs.google.com/spreadsheets/d/${brief?.accountManagerGoogleSheetId}`,
                    "_blank"
                  );
                },
              },
              {
                key: "syncAccountManagerSheet",
                label: "Sync Sheet",
                icon: <SyncOutlined spin={isSyncing && variables?.type === "accountManager"} />,
                title: disableSheet
                  ? "You don't have permission to perform this action."
                  : "",
                disabled: disableSheet || isAccountManagerSheetBusy,
                onClick: () => {
                  keepOpenAndRun(() =>
                    syncGoogleSheet({ type: "accountManager" })
                  );
                },
              },
              {
                key: "downloadAccountManagerXlsx",
                label: "Download XLSX",
                icon: <DownloadOutlined spin={isDownloadingXlsx} />,
                title: disableSheet
                  ? "You don't have permission to perform this action."
                  : "",
                disabled: disableSheet || !briefId || isAccountManagerSheetBusy,
                onClick: () => {
                  if (!briefId) return;
                  keepOpenAndRun(() =>
                    downloadBriefXlsx({ briefId, type: "accountManager" })
                  );
                },
              },
            ]
          : [
              {
                key: "createAccountManagerSheet",
                label: "Create Sheet",
                icon: (
                  <SyncOutlined
                    spin={isSyncing && variables?.type === "accountManager"}
                  />
                ),
                title: disableSheet
                  ? "You don't have permission to perform this action."
                  : "",
                disabled: disableSheet || isAccountManagerSheetBusy,
                onClick: () => {
                  keepOpenAndRun(() =>
                    syncGoogleSheet({ type: "accountManager" })
                  );
                },
              },
              {
                key: "downloadAccountManagerXlsx",
                label: "Download XLSX",
                icon: <DownloadOutlined spin={isDownloadingXlsx} />,
                title: disableSheet
                  ? "You don't have permission to perform this action."
                  : "",
                disabled: disableSheet || !briefId || isAccountManagerSheetBusy,
                onClick: () => {
                  if (!briefId) return;
                  keepOpenAndRun(() =>
                    downloadBriefXlsx({ briefId, type: "accountManager" })
                  );
                },
              },
            ]),
      ],
    },
  ];

  return (
    <>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={handleDropdownOpenChange}
        menu={{ items }}
      >
        {triggerClassName ? (
          <button type="button" className={triggerClassName}>
            Actions
            <ChevronDown size={14} />
          </button>
        ) : (
          <Button icon={<MoreHorizontal size={14} />}>Actions</Button>
        )}
      </Dropdown>
      <Drawer
        open={isDeliverablesDrawerOpen}
        onClose={() => setIsDeliverablesDrawerOpen(false)}
        title="Campaign Deliverables"
        width={800}
        footer={null}
        destroyOnClose
        styles={{
          body: {
            padding: 16,
          },
        }}
      >
        <CampaignDeliverables showTitle={false} />
      </Drawer>
      <Drawer
        open={isManageColumnsOpen}
        width={480}
        title="Manage Columns"
        destroyOnClose
        onClose={() => setIsManageColumnsOpen(false)}
        styles={{
          body: {
            padding: "20px 20px 12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 120px)",
          },
        }}
        footer={null}
      >
        <BriefCustomFields onClose={() => setIsManageColumnsOpen(false)} />
      </Drawer>
      <AddCreator
        isOpen={openCreatorModal}
        onClose={() => setOpenCreatorModal(false)}
      />
      <BulkEditBriefInfluencersModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
      />
    </>
  );
}
